import React, { useEffect } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { hideSnackbar } from "./redux/snackbarSlice";
import { closeTaskPopup, fetchAllActionItems } from "./redux/taskSlice";
import { getAllAppSetups } from "./redux/appSetupSlice";
import Snackbar from "./common/Snackbar";
import HandlePartsFilter from "./common/AppSpecificConpnents/PartsTable/HandlePartsFilter";
import UrlChangeTracker from "./util/UrlChangeTracker";
import AuthValidator from "./components/AuthValidator";
import { RevisionLockProvider } from "./contexts/RevisionLockContext";
import Preview from "./pages/Preview/Preview";
import CopyReplace from "./pages/NodeActions/Copy/CopyReplace";
import VersionHistoryCopyReplace from "./pages/NodeActions/VersionHistoryCopy/VersionHistoryCopyReplace";
import DeleteReplace from "./pages/NodeActions/Delete/DeleteReplace";
import MoveReplace from "./pages/NodeActions/Move/MoveReplace";
import TaskPopup from "./pages/Tasks/TaskPopup";
import AppRoutes from "./AppRoutes";
import UserAttentionPopup from "./pages/Tasks/Popups/UserAttentionPopup";

const App = () => {
  const { open, message, type } = useSelector((state) => state.snackbar);
  const isPreviewOpen = useSelector((state) => state.preview.isOpen);
  const copyReplaceMode = useSelector((state) => state.preview.copyReplaceMode);
  const deleteMode = useSelector((state) => state.preview.deleteMode);
  const moveMode = useSelector((state) => state.preview.moveMode);
  const isVersionHistory = useSelector((state) => state.preview.isVersionHistory);
  const auditTrailId = useSelector((state) => state.preview.auditTrailId);
  const isTaskPopupOpen = useSelector((state) => state.task.isTaskPopupOpen);
  const dispatch = useDispatch();

  // Load app setup data on app start to get timezone configuration.
  // On public routes use the unauthenticated endpoint to avoid a 401 redirect.
  useEffect(() => {
    dispatch(getAllAppSetups());
    dispatch(fetchAllActionItems({searchQuery:"&is_system_gen=true"}));
  }, [dispatch]);

  // Globally prevent invalid characters in numeric-only inputs (e, E, +, -)
  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      if (
        target &&
        target.tagName === "INPUT" &&
        target.type === "number" &&
        (e.key === "e" || e.key === "E" || e.key === "+" || e.key === "-")
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <>
      {/* ri-docmanager-app */}
      {/* ri-doc-front */}
        <Router basename="/test/">
        {/* Revision tab lock: holds lock by URL so it is not released when navigating Document <-> view-revision */}
        <RevisionLockProvider>
        {/* Validate authentication on app load */}
        <AuthValidator />
        {/* Main Application Routes - Hidden when preview is open */}
        <div style={{ display: isPreviewOpen ? "none" : "block" }}>
          <AppRoutes />
          {/* This is for disable parts filter option based on screen */}
          <HandlePartsFilter />
          <UrlChangeTracker />
        </div>

        {/* Preview/CopyReplace/DeleteReplace/MoveReplace Component - Shown when preview is open */}
        {isPreviewOpen && (
          <div style={{ display: "block", width: "100%", minHeight: "100vh" }}>
            {copyReplaceMode ? (
              isVersionHistory ? (
                <VersionHistoryCopyReplace auditTrailId={auditTrailId} />
              ) : (
                <CopyReplace />
              )
            ) : deleteMode ? (
              <DeleteReplace />
            ) : moveMode ? (
              <MoveReplace />
            ) : (
              <Preview />
            )}
          </div>
        )}

        {/* Task Popup - Inside Router for navigation support */}
        {isTaskPopupOpen && (
          <TaskPopup closeModal={() => dispatch(closeTaskPopup())} />
        )}

        <UserAttentionPopup />
        </RevisionLockProvider>
      </Router>

      {open && (
        <Snackbar
          message={message}
          type={type}
          onClose={() => dispatch(hideSnackbar())}
        />
      )}
    </>
  );
};

export default App;
